import {trans as $t} from "@/vue/composables/missingTranslations";
import useDates from "@/libs/dates/useDates";
import { createConfirmDialog as createDialog } from "vuejs-confirm-dialog";
import AppAlert from "@/vue/shared/dialog/AppAlert.vue";
import AppConfirm from "@/vue/shared/dialog/AppConfirm.vue";

export const Debug = {
    data: () => ({
        hidden: true
    }),
    template: /* html */ `
        <!-- prettier-ignore -->
        <div v-if="$root.debug">
            <i
                class="bi-info-square-fill text-secondary h1"
                @click="hidden = !hidden"
                style="
                    position: relative;
                    z-index: 1;
                    display: inline-block;
                    margin: -15px 0px;
                    bottom: -2px;
                "
            />
            <pre
                :hidden="hidden"
                class="debug"
                data-max-height="600px"
                style="
                    max-height: 600px;
                    position: relative;
                    padding: 5px;
                    min-width: 10px;
                    width: fit-content !important;
                    overflow: scroll;
                    z-index: 2;
                    border: 2px solid;
                    background: var(--bs-light);
                    border-radius: var(--bs-border-radius);
                "
            ><slot></slot></pre>
        </div>
    `
};

/**
 * Returns a string with uppercase for start of each word. Examples:
 *   org_location_id -> Org Location Id
 *   org-location-id -> Org Location Id
 *   org-LOCATION-ID -> Org Location Id
 *   ORG LOCATION ID -> Org Location Id
 *
 * @param {string} str
 * @returns {string}
 */
export function startCase(str) {
    const strArray = str.replace(/[-_]/g, " ").split(" ");
    return strArray
        .map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
        .join(" ");
}

export function decimal(val, places = 0) {
    if (isNaN(val)) return val;
    return Number(val).toLocaleString("en", {useGrouping: false, minimumFractionDigits: places});
}

export function limitDecimals(val, places) {
    if (isNaN(val)) return null;
    let numStr = val.toString();

    // Find the index of the decimal point
    let decimalIndex = numStr.indexOf(".");

    // If there's no decimal point, return the number as is
    if (decimalIndex === -1) {
        return numStr;
    }

    // Slice the string up to the first decimal place without rounding
    return numStr.slice(0, decimalIndex + 1 + places);
}

export const COMPLIANCE_MINUTES = 4 * 60; //4 hour threshold (in minutes)

export const yearsFrom = (date) => {
    const theDate = moment(date);
    const currentDate = moment();

    return currentDate.diff(theDate, 'years');
};

export function hoursMinutes(minutesOriginal) {
    const hours = String(Math.floor(minutesOriginal / 60));
    const minutes = String(minutesOriginal % 60);

  return {
        hours,
        minutes,
        formatted: `${hours.padStart(2, 0)}:${minutes.padStart(2, 0)}`
    };
}

let tooltipSummary = {};
export function useSummaryTooltip() {

    const { format } = useDates();



    function setSummary(value) {
        tooltipSummary = value;
    }

    function getTooltipHtml(summary) {
        const {
            record_date,
            date,
            treat_minutes,
            minutesOnVent,
            ahi,
            avg_leak,
            total_p_minutes,
            isComplianceDate,
            isPeriodStartDate,
            isPeriodEndDate,
            created_at,
        } = summary;

        const formatted_date = format(record_date ? record_date : date,'ddd, MMM D, YYYY');

        let blower_time_info = '';
        if (total_p_minutes) {
            blower_time_info = `<span style="font-size: 0.75rem;">(${hoursMinutes(total_p_minutes).formatted})</span>`;
        }

        let minutes = treat_minutes ? treat_minutes : minutesOnVent;
        let summaryDetails = `
            <p class="mb-1">
                <strong>${$t('Usage')}:</strong>
                ${hoursMinutes(isNaN(minutes) ? 0 : minutes).formatted}
                ${blower_time_info}
            </p>
        `;

        if (ahi !== undefined && avg_leak !== undefined) {
            summaryDetails += `
            <p class="mb-1">
                <strong>${$t('AHI')}:</strong>
                ${ahi != null ? decimal(ahi, 1) :  '--'}
            </p>
            <p class="mb-1" v-if="avg_leak">
                <strong>${$t('Avg Leak')}:</strong>
                ${avg_leak ?? '--'} L/min
            </p>
            `;
        }

        if (summary.missing) {
            summaryDetails = `
                <p class="mb-1">
                    <strong>${$t('Missing Day')}</strong>
                </p>
            `;
        }

        let createdAt = `<div class="mt-3 fs-6">
                        ${$t('Created')}: ${format(created_at ? created_at : date, 'YYYY-MM-DD HH:mm')}
                    </div>`;

        if (minutesOnVent) {
            createdAt = '';
        }

        let partialRecordMessage = '';

        if (summary.partial) {
            partialRecordMessage = `<div class="px-3 pb-3 fst-italic" style="text-align: justify; font-size: 0.7rem;">
                 ${$t('messages.preliminary_record_summary_popup_info')}
            </div>`;
        }
        let compliance = '';
        if (isComplianceDate || isPeriodStartDate || isPeriodEndDate) {
            const complianceText = isComplianceDate ? $t('Compliance met') :
                isPeriodStartDate ? $t('Initial Compliance Start') :
                    isPeriodEndDate ? $t('Initial Compliance End') : '';
            compliance = `<div class="fs-5 mt-3"><strong>${complianceText}</strong></div>`;
        }

        return `
        <div class="card" style="border: 1px solid #ccc;">
            <div class="p-3">
                <h4 class="mb-3 text-primary">
                    ${formatted_date}
                </h4>
                <div class="fs-5">
                    ${summaryDetails}
                </div>
                ${createdAt}
                ${compliance}
            </div>
            ${partialRecordMessage}
        </div>
        `;
    }

    function chartjsTooltipHandler(context) {
        // Tooltip Element
        const {chart, tooltip} = context;
        const tooltipEl = getOrCreateTooltip(chart);

        // Hide if no tooltip
        if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
        }

        // Set Text
        tooltipEl.innerHTML = getTooltipHtml(tooltipSummary);

        const { offsetLeft, offsetTop } = chart.canvas;
        window.chart = chart
        let barW = chart.getDatasetMeta(0).data[0].width;

        //console.log('{ offsetLeft, offsetTop}', { offsetLeft, offsetTop }, tooltip)

        // Display, position, and set styles for font
        const tooltipW = tooltipSummary.partial ? 400 : 220;
        const tooltipH = 200;
        tooltipEl.style.opacity = 1;
        tooltipEl.style.width = tooltipW + 'px';

        tooltipEl.className = '';
        tooltipEl.classList.add('chart-tooltip');
        tooltipEl.classList.add('x-'+tooltip.xAlign)
        tooltipEl.classList.add('y-'+tooltip.yAlign)

        let left = offsetLeft + tooltip.caretX;
        if (tooltip.xAlign === 'left') {
            left += tooltipW/2 + barW/2;
        }
        if (tooltip.xAlign === 'right') {
            left -= tooltipW/2 + barW/2;
        }

        let top = (offsetTop + tooltip.caretY) / 2;

        if (tooltipSummary.partial) {
            top -= 20;
        }

        /*
            todo: figure this out if a tootip offset bug is reported
            if (tooltip.yAlign === 'top') {
                top += tooltipH/4;
            }
            if (tooltip.yAlign === 'bottom') {
                top -= tooltipH/4;
            }
         */

        tooltipEl.style.left = left + 'px';
        tooltipEl.style.top = top + 'px';


        function getOrCreateTooltip(chart) {
            let tooltipEl = chart.canvas.parentNode.querySelector('div');
            if (!tooltipEl) {
                tooltipEl = document.createElement('div');
                tooltipEl.classList.add('chart-tooltip');

                tooltipEl.style.pointerEvents = 'none';
                tooltipEl.style.opacity = 1;
                tooltipEl.style.position = 'absolute';
                tooltipEl.style.transform = 'translate(-50%, 0)';
                tooltipEl.style.transition = 'all .1s ease';

                chart.canvas.parentNode.appendChild(tooltipEl);
            }
            return tooltipEl;
        }
    }

    return {
        setSummary,
        getTooltipHtml,
        chartjsTooltipHandler,
    }
}

export function dailySummaryTooltip() {

    function setSummary(value) {
        tooltipSummary = value;
    }

    function getTooltipHtml(summary) {
        const { format } = useDates();
        const formatted_date = format(summary.date, 'ddd, MMM D, YYYY');

        let summaryDetails = ``;

        if (Array.isArray(summary.tooltipInfo)) {
            summary.tooltipInfo.forEach((tooltip) => {
                const item = summary.summary.filter(item => item.name === tooltip.field).slice().shift();
                // console.log(item, summary.summary, tooltip);
                summaryDetails += `
                <p class="mb-1">
                    <strong>${tooltip.label}:</strong>
                    ${typeof item?.average === 'number' ? roundNumber(item?.average) : item?.average}
                </p>
            `;
            });
        } else if(Array.isArray(summary.summary)) {
            const item = summary.summary.filter(item => item.name === summary.tooltipInfo.field).slice().shift();
            summaryDetails = `
                <p class="mb-1">
                    <strong>${summary.tooltipInfo.label}:</strong>
                    ${typeof item?.average === 'number' ? roundNumber(item?.average) : item?.average}
                </p>
            `;
        } else {
            const {
                treat_minutes,
                minutesOnVent,
            } = summary;
            let minutes = treat_minutes ? treat_minutes : minutesOnVent;
            summaryDetails = `
                <p class="mb-1">
                    <strong>Usage:</strong>
                    ${hoursMinutes(isNaN(minutes) ? 0 : minutes).formatted}
                </p>
            `;
        }

        return `
            <div class="card" style="border: 1px solid #ccc;">
                <div class="p-3">
                    <h4 class="mb-3 text-primary">
                        ${formatted_date}
                    </h4>
                    <div class="fs-5">
                        ${summaryDetails}
                    </div>
                    ${summary.ventUsageTemplate ? summary.ventUsageTemplate : ''}
                </div>
            </div>
        `;
    }

    function chartjsTooltipHandler(context) {
        // Tooltip Element
        const {chart, tooltip} = context;
        const tooltipEl = getOrCreateTooltip(chart);

        // Hide if no tooltip
        if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
        }

        // Set Text
        tooltipEl.innerHTML = getTooltipHtml(tooltipSummary);

        const { offsetLeft, offsetTop } = chart.canvas;
        window.chart = chart
        let barW = chart.getDatasetMeta(0).data[0].width;

        // Display, position, and set styles for font
        const tooltipW = 220;
        const tooltipH = 200;
        tooltipEl.style.opacity = 1;
        tooltipEl.style.width = tooltipW + 'px';

        tooltipEl.className = '';
        tooltipEl.classList.add('chart-tooltip');
        tooltipEl.classList.add('x-'+tooltip.xAlign)
        tooltipEl.classList.add('y-'+tooltip.yAlign)

        let left = offsetLeft + tooltip.caretX;
        if (tooltip.xAlign === 'left') {
            left += tooltipW/2 + barW/2;
        }
        if (tooltip.xAlign === 'right') {
            left -= tooltipW/2 + barW/2;
        }

        let top = (offsetTop + tooltip.caretY)/2;
        /*
            todo: figure this out if a tootip offset bug is reported
            if (tooltip.yAlign === 'top') {
                top += tooltipH/4;
            }
            if (tooltip.yAlign === 'bottom') {
                top -= tooltipH/4;
            }
         */
        tooltipEl.style.left = left + 'px';
        tooltipEl.style.top = top + 'px';


        function getOrCreateTooltip(chart) {
            let tooltipEl = chart.canvas.parentNode.querySelector('div');
            if (!tooltipEl) {
                tooltipEl = document.createElement('div');
                tooltipEl.classList.add('chart-tooltip');

                tooltipEl.style.pointerEvents = 'none';
                tooltipEl.style.opacity = 1;
                tooltipEl.style.position = 'absolute';
                tooltipEl.style.transform = 'translate(-50%, 0)';
                tooltipEl.style.transition = 'all .1s ease';

                chart.canvas.parentNode.appendChild(tooltipEl);
            }
            return tooltipEl;
        }
    }

    return {
        setSummary,
        getTooltipHtml,
        chartjsTooltipHandler,
    }
}

let tooltipSettingReading = {};
export function useSettingReadingTooltip() {

    const { formatDateTime } = useDates();

    function setTooltipSetting(value) {
        tooltipSettingReading = value;
    }

    function getTooltipHtml(currentReading) {
        const {
            updateTime,
            timeOfDay,
            setting
        } = currentReading;

        let summaryDetails = `
            <p class="text-center m-0 fw-bold">${setting?.name}</p>
        `;

        if (setting?.units) {
            summaryDetails += `
                <p class="text-center m-0 fw-bold">(${setting?.units})</p>
            `;
        }

        summaryDetails += `
            <p class="text-center m-0 fw-bolder">${setting?.previousValue} -> ${setting?.value}</p>
        `;


        return `
            <div class="card" style="border: 1px solid #ccc;">
                <p class="m-0 bg-soft-secondary text-center">
                    ${updateTime}
                </p>
                <div class="p-3">
                    <div class="fs-5">
                        ${summaryDetails}
                    </div>
                </div>
                <br>
                <p class="m-0 bg-soft-secondary text-center">
                    at ${timeOfDay}
                </p>
            </div>
        `;
    }

    return {
        setTooltipSetting,
        getTooltipHtml
    }
}

export function calculatePercentageBetweenDates(startDate, endDate, date) {
    const unixStartDate = moment(startDate).unix();
    const unixEndDate = moment(endDate).unix();
    const unixDate = moment(date).unix();
    const elapseTime = unixDate - unixStartDate;
    const total = unixEndDate - unixStartDate;
    return (elapseTime / total) * 100;
}

export function treatMinutesUI(item, patient, compliance) {

    let treat_minutes = item.treat_minutes
    if (patient?.use_blower_time) {
        treat_minutes = Math.max(
            item.treat_minutes,
            item.total_p_minutes || 0
        );
    }
    const style = getComputedStyle(document.body);
    const maxMinutes = 60 * 12; // 12 hours (in minutes)
    const threshold = (compliance?.threshold !== null || compliance?.threshold !== undefined) ? compliance.threshold : 60 * 4;

    let treat_minutes_ui = treat_minutes;
    let borderColor = null;
    let backgroundColor;

    const isSuccess = treat_minutes >= threshold;

    backgroundColor = isSuccess ? style.getPropertyValue("--bs-success") : style.getPropertyValue("--bs-danger");

    if (item.partial) {
        if (isSuccess) {
            backgroundColor += "90";
        } else {
            const rgbValues = backgroundColor.match(/\d+/g);

            backgroundColor = `rgba(${rgbValues[0]}, ${rgbValues[1]}, ${rgbValues[2]}, 0.8)`;
        }
    }

    if (treat_minutes === undefined) {
        treat_minutes_ui = maxMinutes;
        backgroundColor = `rgba(var(--bs-dark-rgb), 0.25)!important`;
        backgroundColor = style.getPropertyValue('--bs-gray-400');
    } else if (Number(treat_minutes) === 0) {
        treat_minutes_ui = maxMinutes;
        backgroundColor = 'rgb(255 255 255 / 17%)'
        borderColor = style.getPropertyValue('--bs-danger');
    }

    const styleVertical = `
        width: ${(treat_minutes_ui / maxMinutes) * 100}%;
        background-color: ${backgroundColor};
        border: .15em solid ${borderColor};
    `;

    return {
        treat_minutes,
        treat_minutes_ui,
        backgroundColor,
        borderColor,
        styleVertical,
    };
}

export function mvcVentUsageMinutesUI(secondsOnMode, bgColor) {
    const maxValue = 86400;
    const styleVertical = `
        width: ${(secondsOnMode/maxValue) * 100}%;
        background-color: ${bgColor};
        border: .15em solid ${bgColor};
    `;
    return {
        styleVertical
    };
}

export function mvcMinutesUI(minutesOnVent) {
    const style = getComputedStyle(document.body);
    const maxMinutes = 60 * 12; // 12 hours (in minutes)
    const threshold = 60 * 4; // 4 hours (in minutes)

    let treat_minutes_ui = minutesOnVent;
    let borderColor = minutesOnVent > threshold
        ? null : style.getPropertyValue('--bs-danger');
    let backgroundColor = minutesOnVent > threshold
        ? style.getPropertyValue('--bs-success')
        : style.getPropertyValue('--bs-danger');

    const styleVertical = `
        width: ${(treat_minutes_ui / maxMinutes) * 100}%;
        background-color: ${backgroundColor};
        border: .15em solid ${borderColor};
    `;

    return {
        minutesOnVent,
        treat_minutes_ui,
        backgroundColor,
        borderColor,
        styleVertical,
    };
}

export function sourceTypeIs(source_type) {
    const is = {
        value: null,
        wifi: false,
        qr: false,
        sd: false,
    }
    switch (source_type) {
        case 'ventilator_summaries_fixed':
        case 'ventilator_summaries_v2_fixed':
            is.wifi = is.value = 'wifi';
            break;
        case 'sd_summaries':
        case 'sd_summaries_v2_fixed':
            is.sd = is.value = 'sd';
            break;
        case 'ventilator_brief_summaries_fixed':
            is.qr = is.value = 'qr';
            break;
    }

    return is;
}

// Bootstrap icons: https://icons.getbootstrap.com/
export const sourceIconClasses = {
    ventilator_summaries_fixed: 'bi-wifi',
    ventilator_summaries_v2_fixed: 'bi-wifi',
    sd_summaries: 'bi-sd-card',
    sd_summaries_v2_fixed: 'bi-sd-card',
    ventilator_brief_summaries_fixed: 'bi-qr-code',
};

/**
 * Copied from existing resource keys
 */
export const Patient = () => ({
    //"id": null,
    "assigned_id": null,
    "last_name": null,
    "first_name": null,
    "phone": null,
    "mobile_phone": null,
    "address1": null,
    "address2": null,
    "city": null,
    "state": null,
    "zip": null,
    "current_serial": null,
    "device_pin": null,
    "dme_id": null,
    "clinician_id": null,
    "physician_id": null,
    "username": null,
    "password": null,
    "created": null,
    "modified": null,
    "therapy_start": null,
    "sex": null,
    "last_report": null,
    "birth_date": null,
    "active": null,
    "phone_pin": null,
    "note": null,
    "country": null,
    "adherence_category_id": null,
    "email": null,
    "alert_type": null,
    "org_location_id": null,
    "is_rules_active": null,
    "is_rules_active_updated_by": null,
    "is_rules_active_updated_at": null,
})

export const countInputsWithValue = function (form) {
    return $(form).find('input[type="radio"]:checked, input[type="checkbox"]:checked, select:not(.not-filter), input[type="text"]:not(.not-filter)').filter(function() {
        const value = $(this).val();

        if ($(this).is('select') && $(this).prop('multiple')) {
            return value !== null && value.length > 0;
        }

        return value !== undefined && value !== null && value.trim() !== '';
    });
}

export const getSelectedOptionText = function(selectName, value) {
    const option = document.querySelector(`select[name="${selectName}"] > option[value="${value}"]`);

    return option ? option.innerText : "";
};

export const getSelectedRadioText = function(radioName, value) {
    const radio = document.querySelector(`input[type="radio"][name="${radioName}"][value="${value}"]`);

    return radio ? radio.nextSibling.textContent.trim() : "xx";
};
export const getSelectedNavLinkText = function(value) {
    const option = document.querySelector(`.nav-link[data-type="${value}"]`);

    return option ? option.innerText : '';
}

export const formatIsActive = function(isActive) {
    return isActive ? `<span class="badge bg-success">${$t('Active')}</span>` : `<span class="badge bg-danger">${$t('Inactive')}</span>`;
}

export const formatAndHandleDoubleClick = function(isActive, patientId) {
    const active = $t('Active');
    const inactive = $t('Inactive');
    return `<span class="badge ${isActive ? 'bg-success' : 'bg-danger'}" ondblclick="handlePatientStatusDoubleClick(${isActive}, '${patientId}')">
                ${isActive ? active : inactive}
            </span>`;
}

export const formatIsSuccess = function(exitFlag) {
    if (exitFlag === -1) {
        return `<span class="badge p-1" style="background-color: #8C98A4">${$t('INCOMPLETE')}</span>`;
    } else if (exitFlag === 0) {
        return `<span class="badge bg-warning">${$t('INCOMPLETE')}</span>`;
    } else {
        return `<span class="badge bg-success">${$t('SUCCESS')}</span>`;
    }
};

const BEST_X_DAYS = 30;
const LAST_X_DAYS = 30;

export function getBestXDays(summaries = [], patient = {}) {
    let compliance_threshold_minutes = patient?.compliance_met_result?.threshold;
    if (patient?.compliance_threshold_minutes) {
        compliance_threshold_minutes = patient?.compliance_threshold_minutes;
    }

    const initialState = {
        dateStart: null,
        dateEnd: null,
        daysTotal: patient?.compliance_met_result?.out_of_days,
        totalTreatMinutes: 0,
        daysCompliant: 0,
        daysCompliantPercent: 0,
        items: [],
    }
    const best = { ...initialState };

    const summariesByKey = Object.fromEntries(
        summaries.map(object => [object.record_date, {
            record_date: object.record_date,
            minutes: patient.use_blower_time
                ? Math.max(object.treat_minutes, object.total_p_minutes || 0)
                : object.treat_minutes
        }])
    )
    summaries.every(item => {
        let dateEnd = moment(item.record_date);
        let dateStart = dateEnd.clone().subtract(patient?.compliance_met_result?.out_of_days - 1, 'days');


        // Skip if we don't have a full set of dates in the range
        // "Best Days" not available for range less than 30 days
        const allDatesInRangeExist = Array.from({
            length: patient?.compliance_met_result?.out_of_days || 30
        }, (_, i) => {
            const d = dateStart.clone().add(i, "days").format("YYYY-MM-DD");

            return !!summariesByKey[d];
        }).every(Boolean);

        if (!allDatesInRangeExist) {
            return true; // continue to next window
        }

        const compliant = [];
        let totalMinutes = 0;
        let dX = dateStart.clone();
        while (dX <= dateEnd) {
            const found = summariesByKey[dX.format('YYYY-MM-DD')];
            if (found && found.minutes >= compliance_threshold_minutes) {
                totalMinutes += found.minutes;
                compliant.push(found);
            }
            dX.add(1, 'day');
        }

        if (compliant.length >= best.daysCompliant) {
            if (compliant.length === best.daysCompliant
                && totalMinutes < best.totalTreatMinutes) {
                return 'skip';
            }
            //total_p_minutes
            best.items = compliant.map(i => `${i.record_date} - ${i.minutes} minutes - ${hoursMinutes(i.minutes).formatted}`);
            best.totalTreatMinutes = totalMinutes;
            best.daysCompliant = compliant.length;
            best.daysCompliantPercent = decimal((compliant.length / patient?.compliance_met_result?.out_of_days) * 100, 0);
            best.dateStart = dateStart.format('YYYY-MM-DD');
            best.dateEnd = dateEnd.format('YYYY-MM-DD');
            best.dateDiffInDays = dateEnd.diff(dateStart, 'days');
        }
        /**
         * return `false` to break early
         */
        return true;
    })

    return best;
}

export function getBestXDaysMVC(summaries = [], numDays = 30) {
    const initialState = {
        dateStart: null,
        dateEnd: null,
        daysTotal: numDays,
        totalTreatMinutes: 0,
        daysCompliant: 0,
        daysCompliantPercent: 0,
        items: [],
    }
    const best = { ...initialState };

    const summariesByKey = Object.fromEntries(
        summaries.map(object => [object.record_date, {
            record_date: object.record_date,
            minutes: object.treat_minutes
        }])
    )
    summaries.every(item => {
        let dateEnd = moment(item.record_date);
        let dateStart = dateEnd.clone().subtract(BEST_X_DAYS - 1, 'days');

        if (!'disable range < 30' && ! summariesByKey[dateStart.format('YYYY-MM-DD')]) {
            /**
             * Break early - "Best Days" not available for range less than 30 days
             */
            return false;
        }

        const compliant = [];
        let totalMinutes = 0;
        let dX = dateStart.clone();
        while (dX <= dateEnd) {
            const found = summariesByKey[dX.format('YYYY-MM-DD')];
            if (found && found.minutes >= COMPLIANCE_MINUTES) {
                totalMinutes += found.minutes;
                compliant.push(found);
            }
            dX.add(1, 'day');
        }

        if (compliant.length >= best.daysCompliant) {
            if (compliant.length === best.daysCompliant
                && totalMinutes < best.totalTreatMinutes) {
                return 'skip';
            }
            //total_p_minutes
            best.items = compliant.map(i => `${i.record_date} - ${i.minutes} minutes - ${hoursMinutes(i.minutes).formatted}`);
            best.totalTreatMinutes = totalMinutes;
            best.daysCompliant = compliant.length;
            best.daysCompliantPercent = decimal((compliant.length / numDays) * 100, 0);
            best.dateStart = dateStart.format('YYYY-MM-DD');
            best.dateEnd = dateEnd.format('YYYY-MM-DD');
            best.dateDiffInDays = dateEnd.diff(dateStart, 'days');
        }
        /**
         * return `false` to break early
         */
        return true;
    })

    return best;
}

export const isOrgEquipmentProvider = function(type) {
    return type === 'Equipment Provider';
};

export const isOrgClinical = function(type) {
    return type === 'Clinical';
};

export const isOrgManufacturer = function(type) {
    return type === 'Manufacturer';
};

export const partialStringHide = (value) => {
    const visibleChars = Math.round(value.length / 2);
    const visiblePart = value.substring(0, visibleChars);
    const hiddenPart = "*".repeat(value.length - visibleChars);

    return visiblePart + hiddenPart;
};

export const debounce = function(func, timeout = 300) {
    let timer;

    return (...args) => {
        clearTimeout(timer);

        timer = setTimeout(() => {
            func.apply(this, args);
        }, timeout);
    };
};


export const createAlertDialog = function() {
   return createDialog(AppAlert, {
        title: null,
        message: null
    }, {
        chore: true,
        keepInitial: true
    });
};

export const createConfirmDialog = function() {
    return createDialog(AppConfirm, {
        title: null,
        message: null
    }, {
        chore: true,
        keepInitial: true
    });
};


export const timeAgo = function(date, showDaysOnly = false) {
    let dateObj;

    if (typeof date === "string") {
        dateObj = new Date(date);

        if (isNaN(dateObj.getTime())) {
            throw new Error("Invalid date string");
        }
    } else if (date instanceof Date) {
        dateObj = date;
    } else {
        throw new Error("Invalid input. Please provide a Date object or a valid date string");
    }

    const seconds = Math.floor((dateObj.getTime() - Date.now()) / 1000);
    const absSeconds = Math.abs(seconds);

    if (seconds < 0) {
        let interval = Math.floor(absSeconds / 86400);

        if (interval === 1) {
            return `yesterday`;
        }

        if (interval >= 1) {
            return `${interval} days ago`;
        }

        if (showDaysOnly) {
            return "today";
        }

        interval = Math.floor(absSeconds / 3600);
        if (interval >= 1) {
            return `${interval} hours ago`;
        }

        interval = Math.floor(absSeconds / 60);
        if (interval >= 1) {
            return `${interval} minutes ago`;
        }

        return "just now";
    } else {
        let interval = Math.floor(seconds / 86400);

        if (interval === 1) {
            return `tomorrow`;
        }

        if (interval >= 1) {
            return `in ${interval} days`;
        }

        if (showDaysOnly) {
            return "today";
        }

        interval = Math.floor(seconds / 3600);
        if (interval >= 1) {
            return `in ${interval} hours`;
        }

        interval = Math.floor(seconds / 60);
        if (interval >= 1) {
            return `in ${interval} minutes`;
        }

        return "in a few moments";
    }
};

export const roundNumber = (number) => {
    if (!number) return number;
    return Number(number).toFixed(2);
};

export const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

export const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, function(char) {
        return char.toUpperCase();
    });
};

export const domainValidation = (url) => {
    const domainRegex = /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,6}$/i;
    if (domainRegex.test(url)) {
        return true;
    } else {
        return false;
    }
};

export const convertTo12Hour = (time24) => {
    if (!time24) return "";

    let [hours, minutes] = time24.split(":").map(Number);
    let period = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12;

    let formattedHours = hours.toString().padStart(2, "0");
    let formattedMinutes = minutes.toString().padStart(2, "0");

    return `${formattedHours}:${formattedMinutes} ${period}`;
};

export function delay(ms = 1000) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function _NA(value) {
    if (value === 'N/A' || value === 'n/a') {
        return 'NA';
    }
    return value;
}
